import cx from "classnames";
import { isEqual } from "lodash";
import { FC, JSX, useContext, useMemo, useState } from "react";
import { BenchmarkingDashboardSnpPlanFilterEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardSnpPlanFilterEnum";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton, QESButtonTypeClasses } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FlyoutLink } from "@app/shared/Flyouts";
import MedicareAdvancedFilters from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareAdvancedFilters";
import { MedicareFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareFiltersContext";
import { MedicarePrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicarePrimaryFiltersPayload";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";

const MedicarePrimaryFilters: FC = (): JSX.Element => {
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMedicareFiltersContext = useContext(MedicareFiltersContext);

  const [isAdvancedFiltersDirty, setIsAdvancedFiltersDirty] = useState<boolean>(false);

  const hasFilterData = !!benchmarkingFiltersContext.states.length;

  const applyDisabled = (): boolean => {
    // do not allow button interactions if ANYTHING is loading
    if (benchmarkingFiltersContext.linesOfBusinessLoading || benchmarkingMedicareFiltersContext.primaryFiltersLoading || benchmarkingMedicareFiltersContext.networkFiltersLoading || !hasFilterData) {
      return true;
    }

    const countiesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.map(x => x.toString()).sort((a, b) => a.localeCompare(b)),
      benchmarkingFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
    );

    const membershipCountEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload)?.membershipCount === benchmarkingMedicareFiltersContext.selectedMembershipCount;

    const snpFilterEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload)?.snpFilter === benchmarkingMedicareFiltersContext.selectedSnpFilter;

    const starRatingsEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.starRatings?.sort(),
      benchmarkingMedicareFiltersContext.selectedStarRatings.map(x => x.key).sort()
    );

    const planTypesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.planTypes?.sort(),
      benchmarkingMedicareFiltersContext.selectedPlanTypes.map(x => x.key).sort()
    );
    // in order to avoid unnecessary api calls, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one county, a line of business, a membership, a star rating, a plan type, and an snp filter are not selected
    const anyChanged = !countiesEqual || !membershipCountEqual || !starRatingsEqual || !planTypesEqual || !snpFilterEqual;
    const allSelected = benchmarkingFiltersContext.selectedCounties.length > 0 &&
      !!benchmarkingFiltersContext.selectedLineOfBusiness &&
      !!benchmarkingMedicareFiltersContext.selectedMembershipCount &&
      benchmarkingMedicareFiltersContext.selectedStarRatings.length > 0 &&
      benchmarkingMedicareFiltersContext.selectedPlanTypes.length > 0 &&
      !!benchmarkingMedicareFiltersContext.selectedSnpFilter;

    return !(anyChanged && allSelected);
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    const loading = benchmarkingFiltersContext.linesOfBusinessLoading || benchmarkingMedicareFiltersContext.primaryFiltersLoading;
    const isDefault = benchmarkingFiltersContext.selectedCounties.length === 0 &&
      !benchmarkingFiltersContext.selectedState &&
      benchmarkingMedicareFiltersContext.selectedMembershipCount === MembershipCountTypeEnum.All &&
      benchmarkingMedicareFiltersContext.selectedStarRatings.length === benchmarkingMedicareFiltersContext.mappedStarRatings.length &&
      benchmarkingMedicareFiltersContext.selectedPlanTypes.length === benchmarkingMedicareFiltersContext.mappedPlanTypes.length &&
      benchmarkingMedicareFiltersContext.selectedSnpFilter === BenchmarkingDashboardSnpPlanFilterEnum.All;

    return loading || isDefault;
  }

  const onCBSAMouseOver = (parentKey?: string): void => {
    if (!!parentKey) {
      const cbsa: NestingParentDropdownItem = benchmarkingFiltersContext.counties.find(x => x.key === parentKey);
      benchmarkingFiltersContext.setMousedOverCountyIds(cbsa?.children.map(x => x.key));
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onCountyCheckboxMouseOver = (dropdownItemKey?: string): void => {
    if (!!dropdownItemKey) {
      benchmarkingFiltersContext.setMousedOverCountyIds([dropdownItemKey]);
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onStateChange = (stateAbbreviation: string): void => {
    const state: BenchmarkingState = benchmarkingFiltersContext.states.find(x => x.stateAbbreviation === stateAbbreviation);

    if (!!state) {
      benchmarkingFiltersContext.setSelectedState(state);
    }
  }

  // drives the notification pip on the Advanced Filters button — true whenever any of the
  // advanced-only fields (CMS Star Rating, Plan Type, SNP Plan, Membership Count) are non-default
  const showAdvancedFiltersIndicator = useMemo<boolean>(() => {
    const starRatingsDefault = benchmarkingMedicareFiltersContext.selectedStarRatings.length === benchmarkingMedicareFiltersContext.mappedStarRatings.length;
    const planTypesDefault = benchmarkingMedicareFiltersContext.selectedPlanTypes.length === benchmarkingMedicareFiltersContext.mappedPlanTypes.length;
    const snpFilterDefault = benchmarkingMedicareFiltersContext.selectedSnpFilter === BenchmarkingDashboardSnpPlanFilterEnum.All;
    const membershipCountDefault = benchmarkingMedicareFiltersContext.selectedMembershipCount === MembershipCountTypeEnum.All;

    return !starRatingsDefault || !planTypesDefault || !snpFilterDefault || !membershipCountDefault;
  }, [
    benchmarkingMedicareFiltersContext.selectedStarRatings,
    benchmarkingMedicareFiltersContext.mappedStarRatings,
    benchmarkingMedicareFiltersContext.selectedPlanTypes,
    benchmarkingMedicareFiltersContext.mappedPlanTypes,
    benchmarkingMedicareFiltersContext.selectedSnpFilter,
    benchmarkingMedicareFiltersContext.selectedMembershipCount,
  ]);

  return (
    <div className="ci-sticky-header">
      <div className="row">
        <div className="col-12">
          <label className="label-m mb-2">
            Filters
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <QESSelect
            disabled={benchmarkingFiltersContext.linesOfBusinessLoading || !benchmarkingFiltersContext.linesOfBusiness.length}
            testId="network-benchmarking-dashboard__primary-filters--line-of-business"
            label="Line of Business"
            onChange={(e) => benchmarkingFiltersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
            value={benchmarkingFiltersContext.selectedLineOfBusiness || ""}
          >
            {Object.values(benchmarkingFiltersContext.linesOfBusiness).map((lineOfBusiness, index) => (
              <option
                key={index}
                value={lineOfBusiness}
              >
                {BenchmarkingLineOfBusinessEnum[lineOfBusiness]}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData}
            testId="network-benchmarking-dashboard__primary-filters--state"
            label="State"
            onChange={(e) => onStateChange(e.target.value)}
            value={benchmarkingFiltersContext.selectedState?.stateAbbreviation || ""}
          >
            {benchmarkingFiltersContext.states.map((state, index) => (
              <option
                key={index}
                value={state.stateAbbreviation}
              >
                {state.stateName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <MultiselectNestedDropdown
            containerClassName="d-block"
            customSelectInputText={(!!benchmarkingFiltersContext.rawCounties.length && benchmarkingFiltersContext.rawCounties.length === benchmarkingFiltersContext.selectedCounties.length) ? "All" : null}
            disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData || !benchmarkingFiltersContext.selectedState}
            hasDuplicates={true}
            items={benchmarkingFiltersContext.counties}
            label="Service Area"
            onDropdownItemMouseOver={onCountyCheckboxMouseOver}
            onParentMouseOver={onCBSAMouseOver}
            onSelectionChanged={(selectedCounties: string[]) => { benchmarkingFiltersContext.setSelectedCounties(selectedCounties) }}
            selectedItems={benchmarkingFiltersContext.selectedCounties}
            testId="network-benchmarking-dashboard__primary-filters--county"
          />
        </div>
        <div className="col d-flex">
          <div className="align-self-end">
            <FlyoutLink
              classNames={cx(QESButtonTypeClasses.get("outline"), "position-relative")}
              content={{
                body: <MedicareAdvancedFilters onDirtyChange={setIsAdvancedFiltersDirty} />,
                title: "Advanced Filters",
              }}
              disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData}
              modalContent={{
                body: "You will lose unsaved changes. Continue with closing the sidebar?",
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                title: "Warning",
              }}
              size="40"
              testId="competitive-insights__primary-filters--advanced-filters"
              warnOnClose={isAdvancedFiltersDirty}
            >
              {showAdvancedFiltersIndicator && <div id="competitive-insights__primary-filters--advanced-filters-pip" className="qes-button__notification-pip" />}
              Advanced Filters
            </FlyoutLink>
          </div>
        </div>
        <div className="col d-flex">
          <div className="align-self-end">
            <QESButton
              disabled={applyDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--apply"
              qesType="secondary"
              onClick={() => benchmarkingMedicareFiltersContext.onApplyPrimaryFiltersClick()}
            >
              Apply
            </QESButton>

            <QESButton
              disabled={clearDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--clear"
              qesType="secondary-danger"
              onClick={() => benchmarkingMedicareFiltersContext.onClearPrimaryFiltersClick()}
              className="ms-2"
            >
              Clear
            </QESButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MedicarePrimaryFilters;
